import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

// ── Types ─────────────────────────────────────────────────────────────────────

export type GapType         = 'LIQUIDITY' | 'INTEREST_RATE';
export type PresentationMode = 'FLOW' | 'PRINCIPAL';
export type RowDimension    = 'INSTRUMENT_TYPE' | 'BS_CLASS' | 'CURRENCY';

export interface GapConfiguration {
  id: string;
  name: string;
  description?: string | null;
  gapType: GapType;
  presentationMode: PresentationMode;
  rowDimension: RowDimension;
  segmentationId?: string | null;
  currency?: string | null;
  isPreset: boolean;
  sortOrder: number;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type GapConfigCreate = Omit<GapConfiguration,
  'id' | 'isPreset' | 'sortOrder' | 'createdBy' | 'createdAt' | 'updatedAt'>;
export type GapConfigUpdate = GapConfigCreate & { id: string };

// ── Query keys ────────────────────────────────────────────────────────────────

const KEYS = {
  all:  () => ['gapConfigurations'] as const,
  one:  (id: string) => ['gapConfigurations', id] as const,
};

const BASE = (...parts: string[]) => apiPath('alm', 'gap-configurations', ...parts);

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useGapConfigurations(
  options?: Omit<UseQueryOptions<GapConfiguration[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn:  () => apiGet<GapConfiguration[]>(BASE()),
    ...options,
  });
}

export function useGapConfiguration(
  id: string | undefined,
  options?: Omit<UseQueryOptions<GapConfiguration, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.one(id!),
    enabled:  Boolean(id),
    queryFn:  () => apiGet<GapConfiguration>(BASE(id!)),
    ...options,
  });
}

export function useCreateGapConfiguration() {
  const qc = useQueryClient();
  return useMutation<GapConfiguration, CubixApiError, GapConfigCreate>({
    mutationFn: (body) => apiPost<GapConfiguration>(BASE(), body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}

export function useUpdateGapConfiguration() {
  const qc = useQueryClient();
  return useMutation<GapConfiguration, CubixApiError, GapConfigUpdate>({
    mutationFn: ({ id, ...body }) => apiPut<GapConfiguration>(BASE(id), body),
    onSuccess:  (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all() });
      qc.invalidateQueries({ queryKey: KEYS.one(vars.id) });
    },
  });
}

export function useDeleteGapConfiguration() {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, string>({
    mutationFn: (id) => apiDelete(BASE(id)),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const GAP_TYPE_LABELS: Record<GapType, string> = {
  LIQUIDITY:     'Liquidité',
  INTEREST_RATE: 'Taux',
};

export const PRESENTATION_MODE_LABELS: Record<PresentationMode, string> = {
  FLOW:      'Tombée (FLOW)',
  PRINCIPAL: 'Encours (PRINCIPAL)',
};

export const ROW_DIMENSION_LABELS: Record<RowDimension, string> = {
  INSTRUMENT_TYPE: "Type d'instrument",
  BS_CLASS:        'Classe bilan',
  CURRENCY:        'Devise',
};
